import { KeyRound } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { applyFieldErrors, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@nucleus-commerce/ui/components/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';

import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import { webhookSecretSchema, type WebhookSecretValues } from '../schemas/stripe-schemas';

const KNOWN_FIELDS: ReadonlySet<string> = new Set(['webhookSecret']);
const mapField = (field: string) => (field === 'secret' ? 'webhookSecret' : field);
const DEFAULTS: WebhookSecretValues = { webhookSecret: '' };

export interface WebhookSecretDialogProps {
  /** Where Stripe sends events; shown so the person creates the endpoint there first. */
  webhookUrl: string;
  /** True when a secret is stored and this replaces it. */
  replacing: boolean;
  /** Stores the secret (PUT .../stripe/webhook-secret); a rejection lands in the dialog. */
  onSubmit: (secret: string) => Promise<void>;
}

/**
 * Adds or replaces the Stripe signing secret of a store connected with its own keys. Without it
 * payment and refund events cannot be verified, and the only fix used to be to reconnect.
 */
export function WebhookSecretDialog({ webhookUrl, replacing, onSubmit }: WebhookSecretDialogProps) {
  const { t } = useTranslation(['paymentMethods', 'common']);
  const message = useErrorMessage();
  const [open, setOpen] = useState(false);
  const form = useForm<WebhookSecretValues>({
    resolver: useI18nResolver(webhookSecretSchema),
    defaultValues: DEFAULTS,
  });

  const onOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) form.reset(DEFAULTS);
  };

  const submit = form.handleSubmit(async (values) => {
    try {
      await onSubmit(values.webhookSecret);
      onOpenChange(false);
    } catch (error) {
      if (applyFieldErrors(error, form.setError, { knownFields: KNOWN_FIELDS, mapField })) return;
      form.setError('root.server', { type: 'server', message: message(error) });
    }
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button type="button" variant="outline" size="sm">
          <KeyRound aria-hidden />
          {replacing
            ? t('paymentMethods:stripe.webhookSecret.replace')
            : t('paymentMethods:stripe.webhookSecret.add')}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('paymentMethods:stripe.webhookSecret.title')}</DialogTitle>
          <DialogDescription>
            {t('paymentMethods:stripe.webhookSecret.description')}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={submit} noValidate className="flex flex-col gap-4">
            {form.formState.errors.root?.server?.message ? (
              <Alert variant="destructive" role="alert">
                <AlertDescription>{form.formState.errors.root.server.message}</AlertDescription>
              </Alert>
            ) : null}
            <ol className="text-muted-foreground list-decimal space-y-1 pl-5 text-sm">
              <li>{t('paymentMethods:stripe.webhookSecret.step1')}</li>
              <li>
                {t('paymentMethods:stripe.webhookSecret.step2')}{' '}
                <code className="text-foreground font-mono text-[12px] break-all">
                  {webhookUrl}
                </code>
              </li>
              <li>{t('paymentMethods:stripe.webhookSecret.step3')}</li>
            </ol>
            <FormField
              control={form.control}
              name="webhookSecret"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('paymentMethods:stripe.webhookSecret.label')}</FormLabel>
                  <FormControl>
                    <Input
                      autoComplete="off"
                      spellCheck={false}
                      placeholder="whsec_…"
                      className="font-mono text-[13px]"
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>{t('paymentMethods:stripe.webhookSecret.hint')}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
                {t('common:actions.cancel')}
              </Button>
              <Button type="submit" disabled={form.formState.isSubmitting}>
                {form.formState.isSubmitting
                  ? t('common:actions.saving')
                  : t('paymentMethods:stripe.webhookSecret.submit')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
